import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { selectToken } from '../../../util/tokenSelector.js';
import useForwardedRef from '../../../util/useForwardedRef.js';
import useUniqueId from '../../../util/useUniqueId.js';
import { Box } from '../../Box/Box.js';
import '../Common/index.js';
import { LabelStyled, InputTypographyStyled, SkeletonInputContainerStyled } from '../Common/FormField.styled.js';
import { IconWrapper } from '../Common/IconWrapper.js';
import { inputInteractiveState } from '../Common/inputInteractiveState.js';
import { InputFrame as InputFrameMemo } from '../InputFrame/InputFrame.js';
import { Image as ImageMemoized } from '../../Image/Image.js';
import { TextLink } from '../../TextLink/TextLink.js';
import { Typography } from '../../Typography/Typography.js';
import { useExperimentContext } from '../../../contexts/session/Experiment.js';
import { useThemeContext } from '../../../contexts/session/Theme.js';
import styled from 'styled-components';
import { extractFlexItemProps } from '../../../types/Props.js';
import { labelColorToken, isIcon, resolveTextAreaRowsProps, getInputBoxProps } from '../Common/InputHelper.js';

var _excluded = ["label", "type", "intent", "value", "minLength", "maxLength", "leadingAsset", "trailingAsset", "showPasswordLabel", "clearLabel", "persistentLabel", "placeholder", "placeholderTextColor", "placeholderHoverFocusTextColor", "readOnly", "helperText", "helperTextRole", "prefix", "suffix", "onChange", "onFocus", "onBlur", "onClear", "state", "children", "m", "mt", "mb", "mx", "my", "ms", "me", "rows", "minRows", "maxRows", "variant", "required", "selectOnFocus", "enableTrailingControls", "aria-label", "aria-labelledby", "removeExtraSpace", "helperTextData"];
var getAriaAttributes = function getAriaAttributes(id, intent, helperText) {
  if (!helperText) return {};
  if (intent === 'error') return {
    'aria-describedby': id,
    'aria-invalid': true
  };
  return {
    'aria-describedby': id
  };
};
/** Remove browser autofill styles due to the complexity of InputBase */
var StyledBox = /*#__PURE__*/styled(Box).withConfig({
  displayName: "InputBase__StyledBox",
  componentId: "sc-53odve-0"
})([":-webkit-autofill,:-webkit-autofill:hover,:-webkit-autofill:focus,:-webkit-autofill:active{transition:background-color 50000s ease-in-out 0s !important;}&::placeholder{color:", ";opacity:", ";}&:hover::placeholder,&:focus::placeholder{color:", ";opacity:", ";}"], function (_ref) {
  var $placeholderColor = _ref.$placeholderColor;
  return $placeholderColor !== null && $placeholderColor !== void 0 ? $placeholderColor : 'revert';
}, function (_ref2) {
  var $placeholderColor = _ref2.$placeholderColor;
  return $placeholderColor ? 1 : 'revert';
}, function (_ref3) {
  var $placeholderFocusHoverColor = _ref3.$placeholderFocusHoverColor;
  return $placeholderFocusHoverColor !== null && $placeholderFocusHoverColor !== void 0 ? $placeholderFocusHoverColor : 'revert';
}, function (_ref4) {
  var $placeholderFocusHoverColor = _ref4.$placeholderFocusHoverColor;
  return $placeholderFocusHoverColor ? 1 : 'revert';
});
var defaultProps = {
  variant: 'input',
  type: 'text',
  intent: 'default',
  state: 'enabled'
};
var MAX_LINES = 10;

/** INTERNAL component with shared logic between ADS Input and Dropdown */
var InputBase = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _ref5;
  var experiments = useExperimentContext();
  var theme = useThemeContext();
  var label = props.label,
    type = props.type,
    intent = props.intent,
    value = props.value,
    minLength = props.minLength,
    maxLength = props.maxLength,
    propLeadingAsset = props.leadingAsset,
    propTrailingAsset = props.trailingAsset,
    showPasswordLabel = props.showPasswordLabel,
    clearLabel = props.clearLabel,
    persistentLabel = props.persistentLabel,
    placeholder = props.placeholder,
    placeholderTextColor = props.placeholderTextColor,
    placeholderHoverFocusTextColor = props.placeholderHoverFocusTextColor,
    readOnly = props.readOnly,
    propHelperText = props.helperText,
    helperTextRole = props.helperTextRole,
    propPrefix = props.prefix,
    propSuffix = props.suffix,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onClear = props.onClear,
    state = props.state,
    children = props.children,
    m = props.m,
    mt = props.mt,
    mb = props.mb,
    mx = props.mx,
    my = props.my,
    ms = props.ms,
    me = props.me,
    _props$rows = props.rows,
    rowsProp = _props$rows === void 0 ? 0 : _props$rows,
    minRowsProp = props.minRows,
    maxRowsProp = props.maxRows,
    variant = props.variant,
    required = props.required,
    selectOnFocus = props.selectOnFocus,
    _props$enableTrailing = props.enableTrailingControls,
    enableTrailingControls = _props$enableTrailing === void 0 ? true : _props$enableTrailing,
    ariaLabel = props['aria-label'],
    ariaLabelledBy = props['aria-labelledby'],
    removeExtraSpace = props.removeExtraSpace,
    helperTextData = props.helperTextData,
    rest = _objectWithoutProperties(props, _excluded);
  var disabled = state === 'disabled';
  var inputType = type !== 'textarea' && type !== 'select' ? type : undefined;
  var hasText = type !== 'select';
  var isTypePassword = inputType === 'password';
  var normalIconColor = variant === 'skeleton' || type === 'select' ? 'deprecated.secondary.interactive' : 'base.subtle';
  var iconColor = disabled ? 'base.disabled' : normalIconColor;
  var textColor = labelColorToken[disabled ? 'disabled' : intent || 'default'];
  var partTextColor = disabled ? 'base.disabled' : 'base.subtle';
  var isZoomFontSizeFix = experiments.isZoomFontSizeFix;
  var typographyVariant = isZoomFontSizeFix ? 'body-default' : 'body-s';
  var topSpacing = selectToken(theme.spacing, '12');
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showPassword = _React$useState2[0],
    setShowPassword = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    active = _React$useState4[0],
    setActive = _React$useState4[1];
  var clearButtonRef = React.useRef();
  var inputRef = useForwardedRef(ref);
  var helperTextId = useUniqueId();
  var labelId = useUniqueId();
  var prefix = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: typographyVariant,
      textColor: partTextColor
    }, propPrefix);
  }, [partTextColor, propPrefix, typographyVariant]);
  var suffix = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: typographyVariant,
      textColor: partTextColor,
      ms: "8"
    }, propSuffix);
  }, [partTextColor, propSuffix, typographyVariant]);
  var additionalAssetProps = React.useMemo(function () {
    return {
      display: 'flex',
      py: '12',
      borderWidth: '0',
      cursor: disabled ? 'auto' : 'text',
      whiteSpace: 'nowrap',
      alignItems: 'center'
    };
  }, [disabled]);
  var leadingElement = React.useMemo(function () {
    if (!propLeadingAsset) return null;
    return isIcon(propLeadingAsset) ? /*#__PURE__*/React.createElement(IconWrapper, {
      id: propLeadingAsset.id,
      color: iconColor,
      size: variant === 'input' ? '16' : '20'
    }) : /*#__PURE__*/React.createElement(Box, {
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(ImageMemoized.Decorative, {
      src: propLeadingAsset.src,
      width: "24px",
      maxWidth: "24px",
      maxHeight: "24px"
    }));
  }, [iconColor, propLeadingAsset, variant]);
  var leading = React.useMemo(function () {
    return (leadingElement || propPrefix) && /*#__PURE__*/React.createElement(Box, _extends({}, additionalAssetProps, {
      py: propLeadingAsset ? '12' : '16',
      ps: variant === 'input' ? '16' : undefined,
      pe: variant === 'skeleton' ? '12' : undefined
    }), propPrefix && prefix, leadingElement);
  }, [additionalAssetProps, leadingElement, prefix, propLeadingAsset, propPrefix, variant]);
  var useFloatingTrailing = !hasText;
  var trailingElement = React.useMemo(function () {
    if (useFloatingTrailing || !propTrailingAsset) return null;
    return isIcon(propTrailingAsset) ? /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      cursor: "pointer",
      as: "button",
      type: "button",
      p: "0",
      tabIndex: 0,
      onClick: propTrailingAsset.onClick
    }, /*#__PURE__*/React.createElement(IconWrapper, {
      id: propTrailingAsset.id,
      label: propTrailingAsset.label,
      color: iconColor,
      size: variant === 'input' ? '16' : '20'
    })) : /*#__PURE__*/React.createElement(TextLink, {
      label: propTrailingAsset.label,
      textVariant: typographyVariant,
      onClick: propTrailingAsset.onClick
    });
  }, [iconColor, propTrailingAsset, useFloatingTrailing, variant, typographyVariant]);
  var onShowPasswordClick = React.useCallback(function () {
    setShowPassword(function (v) {
      return !v;
    });
  }, []);
  var updateActiveState = React.useCallback(function () {
    setActive(document.activeElement === clearButtonRef.current || document.activeElement === inputRef.current);
  }, [inputRef]);
  var isHTMLInputElement = function isHTMLInputElement(element) {
    return element.tagName === 'INPUT';
  };
  var handleOnFocus = React.useCallback(function (e) {
    if (e.currentTarget !== clearButtonRef.current) {
      onFocus === null || onFocus === void 0 || onFocus(e);
    }
    if (selectOnFocus && e.currentTarget && isHTMLInputElement(e.currentTarget)) {
      e.currentTarget.select();
    }
    updateActiveState();
  }, [onFocus, updateActiveState, selectOnFocus]);
  var handleOnBlur = React.useCallback(function (e) {
    if (e.currentTarget !== clearButtonRef.current) {
      onBlur === null || onBlur === void 0 || onBlur(e);
    }
    updateActiveState();
  }, [onBlur, updateActiveState]);
  var isTextarea = type === 'textarea';
  var _React$useMemo = React.useMemo(function () {
      return resolveTextAreaRowsProps(rowsProp, minRowsProp, maxRowsProp);
    }, [rowsProp, minRowsProp, maxRowsProp]),
    isAutogrow = _React$useMemo.isAutogrow,
    HTMLTextAreaRows = _React$useMemo.HTMLTextAreaRows,
    minRows = _React$useMemo.minRows,
    maxRows = _React$useMemo.maxRows;
  var rows = HTMLTextAreaRows || minRows || maxRows;
  var hasRows = (_ref5 = !!rows && rows > 1 && rows <= MAX_LINES) !== null && _ref5 !== void 0 ? _ref5 : false;
  var isTextAreaAutoGrow = isTextarea && isAutogrow;
  var lineHeight = theme.typography[typographyVariant].lineHeight;
  var baseTextAreaHeight = React.useMemo(function () {
    return "calc(".concat(minRows, " * ").concat(lineHeight, " + ").concat(topSpacing, ")");
  }, [minRows, lineHeight, topSpacing]);
  var maxTextAreaHeight = React.useMemo(function () {
    return "calc(".concat(maxRows, " * ").concat(lineHeight, " + ").concat(topSpacing, ")");
  }, [maxRows, lineHeight, topSpacing]);
  var autoResizeTextarea = React.useCallback(function () {
    if (!(inputRef !== null && inputRef !== void 0 && inputRef.current)) return;
    inputRef.current.style.height = baseTextAreaHeight;
    inputRef.current.style.height = "".concat(inputRef.current.scrollHeight, "px");
  }, [inputRef, baseTextAreaHeight]);
  React.useLayoutEffect(function () {
    if (isTextAreaAutoGrow) {
      autoResizeTextarea();
    }
  }, [isTextAreaAutoGrow, autoResizeTextarea, value]);
  var clearValue = React.useCallback(function () {
    var _inputRef$current;
    onChange === null || onChange === void 0 || onChange({
      currentTarget: {
        value: ''
      }
    });
    onClear === null || onClear === void 0 || onClear();
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
  }, [inputRef, onChange, onClear]);
  var hasTrailingSection = !!(!useFloatingTrailing && (propTrailingAsset || propSuffix || hasText));
  var canShowPasswordTrailingElement = isTypePassword;
  var canShowClearButton = !isTypePassword && !readOnly && enableTrailingControls;
  var canShowSuffix = propSuffix;
  var canShowTrailingElement = (!hasText || !active || readOnly) && trailingElement;
  var displayTrailingItems = canShowPasswordTrailingElement || canShowClearButton || canShowSuffix || canShowTrailingElement;
  var trailing = hasTrailingSection && /*#__PURE__*/React.createElement(Box, _extends({}, additionalAssetProps, {
    alignItems: isTextarea ? 'flex-start' : 'center',
    pt: isTextAreaAutoGrow ? '16' : undefined,
    pe: variant === 'input' ? '16' : undefined
  }, !displayTrailingItems && removeExtraSpace ? {
    display: 'none'
  } : {}), canShowPasswordTrailingElement && /*#__PURE__*/React.createElement(Box, {
    as: "button",
    display: "flex",
    p: "0",
    onClick: onShowPasswordClick
  }, /*#__PURE__*/React.createElement(IconWrapper, {
    id: showPassword ? 'fill.symbol.eye-no' : 'fill.symbol.eye',
    color: "base.subtle",
    label: showPasswordLabel,
    size: "24",
    className: 'ads-input-action'
  })), canShowClearButton && /*#__PURE__*/React.createElement(Box, {
    as: "button",
    type: "button",
    display: "flex",
    p: "0",
    ref: clearButtonRef,
    opacity: active && value ? 1 : 0,
    tabIndex: active && value ? undefined : -1,
    pointerEvents: active && value ? undefined : 'none',
    onMouseDown: clearValue,
    onClick: clearValue,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    "aria-label": clearLabel,
    "aria-hidden": active && value ? undefined : true
  }, /*#__PURE__*/React.createElement(IconWrapper, {
    id: variant === 'input' ? 'line.symbol.close' : 'fill.symbol.circle-close',
    color: "deprecated.secondary.interactive",
    label: clearLabel,
    size: variant === 'input' ? '16' : '20',
    className: 'ads-input-action'
  })), canShowSuffix && suffix, canShowTrailingElement && trailingElement);
  var floatingTrailing = useFloatingTrailing && propTrailingAsset && isIcon(propTrailingAsset) && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
    position: "absolute",
    height: "100%",
    role: "presentation",
    end: 0,
    cursor: "auto",
    pe: "16",
    py: "12"
  }, /*#__PURE__*/React.createElement(IconWrapper, {
    id: propTrailingAsset.id,
    color: iconColor,
    size: "16"
  }));
  var interactiveState = inputInteractiveState(experiments, theme, !disabled);
  var inputBoxProps = getInputBoxProps(props, isTextarea, isTextAreaAutoGrow, hasRows, disabled, handleOnFocus, handleOnBlur, !!props.trailingAsset);
  var input = type === 'select' ? /*#__PURE__*/React.createElement(Box, _extends({}, inputBoxProps, rest, getAriaAttributes(helperTextId, intent, propHelperText), required && {
    required: true
  }, {
    ref: inputRef,
    as: type
  }), placeholder && /*#__PURE__*/React.createElement("option", {
    value: ""
  }, placeholder || label), children) : /*#__PURE__*/React.createElement(StyledBox, _extends({}, inputBoxProps, rest, getAriaAttributes(helperTextId, intent, propHelperText), {
    ref: inputRef,
    forwardedAs: isTextarea ? 'textarea' : 'input',
    type: showPassword && isTypePassword ? 'text' : inputType,
    minLength: minLength,
    maxLength: maxLength,
    readOnly: readOnly,
    height: isTextarea && !hasRows && !isTextAreaAutoGrow ? "calc(120px - ".concat(topSpacing, ")") : undefined,
    minHeight: isTextAreaAutoGrow ? baseTextAreaHeight : undefined,
    maxHeight: isTextAreaAutoGrow ? maxTextAreaHeight : undefined,
    rows: isTextarea && hasRows && !isTextAreaAutoGrow ? rows : undefined,
    "aria-label": ariaLabel || (variant === 'skeleton' ? label : undefined),
    "aria-labelledby": ariaLabelledBy,
    $placeholderColor: placeholderTextColor,
    $placeholderFocusHoverColor: placeholderHoverFocusTextColor
  }, placeholder ? {
    placeholder: placeholder
  } : undefined, required && {
    required: true
  }));
  var _extractFlexItemProps = extractFlexItemProps(rest),
    flexItemProps = _extractFlexItemProps.flexItemProps;
  var labelElement = /*#__PURE__*/React.createElement(Box, {
    as: "label",
    display: "flex",
    position: "relative",
    width: "100%"
  }, leading, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flex: 1,
    position: "relative",
    alignItems: "stretch",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(LabelStyled, {
    position: "absolute",
    width: "100%",
    height: "100%",
    display: "flex",
    borderWidth: "0",
    overflow: "hidden",
    ps: leading ? '8' : '16',
    pt: "16"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: typographyVariant,
    textColor: textColor
  }, placeholder || label)), /*#__PURE__*/React.createElement(InputTypographyStyled, {
    variant: typographyVariant,
    tag: "span"
  }, input)), trailing, floatingTrailing);

  // TODO: ADSWEB-1380 Refactor Input component to use Skeleton as InputBase
  return variant === 'input' ? /*#__PURE__*/React.createElement(InputFrameMemo, _extends({
    showLabel: (persistentLabel !== null && persistentLabel !== void 0 ? persistentLabel : false) || value !== undefined && value !== '',
    component: "input",
    state: state !== null && state !== void 0 ? state : defaultProps.state,
    label: label,
    intent: intent !== null && intent !== void 0 ? intent : defaultProps.intent,
    labelId: labelId,
    helperText: propHelperText,
    helperTextId: helperTextId,
    helperTextRole: helperTextRole,
    helperTextData: helperTextData,
    m: m,
    mt: mt,
    mb: mb,
    mx: mx,
    my: my,
    ms: ms,
    me: me
  }, flexItemProps), labelElement) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SkeletonInputContainerStyled, _extends({
    $interactiveState: interactiveState,
    display: "flex",
    position: "relative",
    width: "100%",
    alignItems: "center"
  }, flexItemProps), leading, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flex: 1,
    position: "relative",
    flexDirection: "column",
    alignItems: "stretch"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: typographyVariant,
    tag: "span"
  }, input)), trailing));
});
InputBase.defaultProps = defaultProps;
var InputBaseMemoized = /*#__PURE__*/React.memo(InputBase);
InputBaseMemoized.displayName = 'InputBase';

export { InputBaseMemoized as InputBase };
//# sourceMappingURL=InputBase.js.map
